import * as z from "zod";
import type { AskRedoUIMessage } from "../types";

/**
 * Module-level registry of in-flight browser-executed tool runs.
 *
 * Flow: the agent calls a tool with no server-side execute; the server
 * parks it and the stream closes. `onToolCall` (in `use-ask-redo-chat`)
 * recognises the tool name from the page-registered executors, marks the
 * run here, executes, and writes the output onto the chat's tool part via
 * `addToolOutput`. The custom `sendAutomaticallyWhen` predicate consults
 * this store to auto-resume the stream, and the transport drains it to
 * build the `clientToolResults` RPC payload.
 *
 * Module-level (not React state) for the same reason as the transport's
 * `latestPageContext`: the Chat instance is cached across mounts, so any
 * mount's writes must be visible to the cached instance's callbacks.
 */

interface ClientToolRun {
  toolName: string;
  status: "running" | "done";
}

const runsByToolCallId = new Map<string, ClientToolRun>();

export function markClientToolRunning(
  toolCallId: string,
  toolName: string,
): void {
  runsByToolCallId.set(toolCallId, { toolName, status: "running" });
}

/** Call BEFORE `addToolOutput` — addToolOutput synchronously evaluates
 *  `sendAutomaticallyWhen`, which must already see this run as done. */
export function markClientToolDone(toolCallId: string): void {
  const run = runsByToolCallId.get(toolCallId);
  if (run) {
    run.status = "done";
  }
}

/** Tool outputs come off UI message parts typed as `unknown`; the wire
 *  schema wants JSON. Validate (rather than assert) at this boundary. */
const JsonOutputSchema = z.json();

export interface ClientToolResultPayload {
  toolCallId: string;
  output: z.infer<typeof JsonOutputSchema>;
  isError?: boolean;
}

function toJsonOutput(value: unknown): z.infer<typeof JsonOutputSchema> {
  const parsed = JsonOutputSchema.safeParse(value);
  return parsed.success
    ? parsed.data
    : { error: "Tool output was not JSON-serializable" };
}

type AskRedoMessagePart = AskRedoUIMessage["parts"][number];

function lastAssistantMessage(
  messages: readonly AskRedoUIMessage[],
): AskRedoUIMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role === "assistant") {
      return m;
    }
  }
  return undefined;
}

function finishedToolPart(
  part: AskRedoMessagePart,
  toolCallId: string,
):
  | { state: "output-available"; output: unknown }
  | { state: "output-error"; errorText?: string }
  | undefined {
  if (part.type !== "dynamic-tool" || part.toolCallId !== toolCallId) {
    return undefined;
  }
  if (part.state === "output-available") {
    return { state: "output-available", output: part.output };
  }
  if (part.state === "output-error") {
    return { state: "output-error", errorText: part.errorText };
  }
  return undefined;
}

/**
 * `sendAutomaticallyWhen` predicate half: true when every tracked run has
 * finished and landed its output on the last assistant message — i.e. the
 * stream should auto-resume to deliver the results to the agent.
 *
 * Runs whose tool call no longer exists on the last assistant message
 * (stale runs from an abandoned turn) are dropped so they can't wedge the
 * predicate.
 */
export function clientToolResultsReadyToSend(
  messages: readonly AskRedoUIMessage[],
): boolean {
  if (runsByToolCallId.size === 0) {
    return false;
  }
  const assistant = lastAssistantMessage(messages);
  for (const [toolCallId, run] of runsByToolCallId) {
    const part = assistant?.parts.find(
      (p) => finishedToolPart(p, toolCallId) !== undefined,
    );
    if (!part) {
      if (run.status === "done") {
        // Output never landed on the current conversation (e.g. the turn
        // was regenerated away) — drop the orphan.
        runsByToolCallId.delete(toolCallId);
      } else if (run.status === "running") {
        return false;
      }
      continue;
    }
    if (run.status === "running") {
      return false;
    }
  }
  return runsByToolCallId.size > 0;
}

/**
 * Drains finished runs into the RPC payload. Called by the transport at
 * send time; drained runs are removed so the predicate goes quiet and the
 * resume can't double-send.
 */
export function takeClientToolResults(
  messages: readonly AskRedoUIMessage[],
): ClientToolResultPayload[] {
  if (runsByToolCallId.size === 0) {
    return [];
  }
  const assistant = lastAssistantMessage(messages);
  if (!assistant) {
    return [];
  }
  const results: ClientToolResultPayload[] = [];
  for (const [toolCallId, run] of runsByToolCallId) {
    if (run.status !== "done") {
      continue;
    }
    for (const part of assistant.parts) {
      const finished = finishedToolPart(part, toolCallId);
      if (!finished) {
        continue;
      }
      results.push(
        finished.state === "output-available"
          ? { toolCallId, output: toJsonOutput(finished.output ?? {}) }
          : {
              toolCallId,
              output: { error: finished.errorText ?? "Tool execution failed" },
              isError: true,
            },
      );
      runsByToolCallId.delete(toolCallId);
      break;
    }
  }
  return results;
}

/** Test-only escape hatch. */
export function clearClientToolRunsForTesting(): void {
  runsByToolCallId.clear();
}
